/**
 * Volatility Ratio (Schwager)
 *
 * Measures today's true range relative to the N-period ATR.
 * Values > 1.0 indicate an expansion in volatility.
 * VR = True Range / ATR(period)
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

export const volatilityRatioIndicator: IndicatorDefinition = {
  id: "volatility_ratio",
  name: "Volatility Ratio",
  shortName: "VR",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 100 },
  ],
  plots: [
    { key: "vr", label: "VR", color: "#EF5350", style: "line", lineWidth: 2 },
    { key: "threshold", label: "Threshold", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const atr = computeATR(candles, period);
    const vrData: { time: number; value: number }[] = [];
    const thresholdData: { time: number; value: number }[] = [];

    for (let i = 1; i < candles.length; i++) {
      if (atr[i] === undefined || atr[i] === 0) continue;

      const tr = Math.max(
        candles[i].high - candles[i].low,
        Math.abs(candles[i].high - candles[i - 1].close),
        Math.abs(candles[i].low - candles[i - 1].close)
      );

      vrData.push({ time: candles[i].time, value: tr / atr[i] });
      thresholdData.push({ time: candles[i].time, value: 1.0 });
    }

    return [
      { key: "vr", data: vrData },
      { key: "threshold", data: thresholdData },
    ];
  },
};
